import { useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Camera, X, RefreshCw, Check, AlertCircle } from 'lucide-react';
import { Button } from './Button';

interface CameraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (imageData: string) => void;
  title?: string;
}

export function CameraModal({ isOpen, onClose, onCapture, title = 'Tomar Foto' }: CameraModalProps) {
  const webcamRef = useRef<Webcam>(null);
  const [image, setImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const capture = useCallback(() => {
    if (!webcamRef.current) return;
    
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      setImage(imageSrc);
      setError(null);
    } else {
      setError('No se pudo capturar la imagen. Verifica los permisos de la cámara.');
    }
  }, [webcamRef]);

  const retake = () => {
    setImage(null);
    setError(null);
  };

  const confirmCapture = () => {
    if (image) {
      onCapture(image);
      setImage(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Camera size={20} className="text-blue-500" />
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="relative bg-gray-900 rounded-lg overflow-hidden aspect-video">
            {image ? (
              <img
                src={image}
                alt="Captura"
                className="w-full h-full object-contain"
              />
            ) : (
              <Webcam
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="w-full h-full object-cover"
                videoConstraints={{
                  facingMode: 'environment',
                }}
                onUserMediaError={() => {
                  setError('No se pudo acceder a la cámara. Verifica los permisos.');
                }}
              />
            )}
          </div>

          <div className="flex justify-center gap-3 mt-4">
            {image ? (
              <>
                <Button
                  variant="outline"
                  onClick={retake}
                  className="flex items-center gap-2"
                >
                  <RefreshCw size={18} />
                  Reintentar
                </Button>
                <Button
                  onClick={confirmCapture}
                  className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                >
                  <Check size={18} />
                  Usar Foto
                </Button>
              </>
            ) : (
              <Button
                onClick={capture}
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                disabled={!!error}
              >
                <Camera size={18} />
                Tomar Foto
              </Button>
            )}
          </div>

          <div className="mt-3 text-center">
            <p className="text-xs text-gray-400">
              {image ? '¿Quieres usar esta foto o tomarla de nuevo?' : 'Asegúrate de tener buena iluminación'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}